import { useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { categories } from "@/mocks/comics";

const navLinks = [
  { label: "首页", to: "/" },
  { label: "排行榜", to: "/#rank" },
  { label: "完本推荐", to: "/#wanben" },
  { label: "关于我们", to: "/about" },
  { label: "常见问题", to: "/faq" },
  { label: "联系我们", to: "/contact" },
];

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [catOpen, setCatOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const catRef = useRef<HTMLDivElement>(null);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
    setCatOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    const close = (e: MouseEvent) => {
      if (catRef.current && !catRef.current.contains(e.target as Node)) {
        setCatOpen(false);
      }
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, []);

  const goAnchor = (to: string) => {
    const hash = to.split("#")[1];
    if (location.pathname === "/") {
      const el = document.getElementById(hash);
      if (el) {
        el.scrollIntoView({ behavior: "smooth" });
        return;
      }
    }
    navigate("/", { replace: false });
    setTimeout(() => {
      const el = document.getElementById(hash);
      if (el) el.scrollIntoView({ behavior: "smooth" });
    }, 120);
  };

  return (
    <header
      className={`fixed inset-x-0 top-0 z-50 transition-all duration-300 ${
        scrolled ? "bg-background-950/90 backdrop-blur-md border-b border-background-800" : "bg-transparent"
      }`}
    >
      <nav className="mx-auto flex h-16 md:h-[72px] max-w-7xl items-center justify-between px-4 md:px-6">
        <Link to="/" className="flex items-center gap-2 whitespace-nowrap cursor-pointer">
          <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-gradient-to-br from-primary-500 to-accent-600 text-background-950">
            <i className="ri-fire-fill text-lg" />
          </span>
          <span className="font-heading text-xl md:text-2xl font-bold tracking-wide text-foreground-950">
            虚空<span className="text-gradient-gold">漫画</span>
          </span>
        </Link>

        <div className="hidden items-center gap-1 md:flex">
          {navLinks.map((link) =>
            link.label === "排行榜" || link.label === "完本推荐" ? (
              <button
                key={link.label}
                onClick={() => goAnchor(link.to)}
                className="cursor-pointer whitespace-nowrap rounded-md px-3 py-2 text-sm font-medium text-foreground-600 transition-colors hover:bg-background-800 hover:text-foreground-950"
              >
                {link.label}
              </button>
            ) : (
              <Link
                key={link.label}
                to={link.to}
                className={`cursor-pointer whitespace-nowrap rounded-md px-3 py-2 text-sm font-medium transition-colors ${
                  location.pathname === link.to
                    ? "text-primary-400"
                    : "text-foreground-600 hover:bg-background-800 hover:text-foreground-950"
                }`}
              >
                {link.label}
              </Link>
            )
          )}

          <div ref={catRef} className="relative ml-1">
            <button
              onClick={() => setCatOpen((v) => !v)}
              className="flex cursor-pointer items-center gap-1 whitespace-nowrap rounded-md border border-background-700 px-3 py-2 text-sm font-medium text-foreground-600 transition-colors hover:border-primary-500/50 hover:text-foreground-950"
            >
              <i className="ri-compass-3-line text-base" />
              分类
              <i className={`ri-arrow-down-s-line transition-transform ${catOpen ? "rotate-180" : ""}`} />
            </button>
            {catOpen && (
              <div className="absolute right-0 top-full mt-2 w-56 overflow-hidden rounded-xl border border-background-700 bg-background-900 p-1.5 shadow-xl">
                {categories.map((c) => (
                  <Link
                    key={c.slug}
                    to={`/category/${c.slug}`}
                    className="flex cursor-pointer items-center justify-between rounded-lg px-3 py-2.5 text-sm text-foreground-700 transition-colors hover:bg-background-800 hover:text-foreground-950"
                  >
                    {c.name}
                    <span className="text-xs text-foreground-500">{c.desc.slice(0, 6)}</span>
                  </Link>
                ))}
              </div>
            )}
          </div>
        </div>

        <button
          onClick={() => setOpen((v) => !v)}
          aria-label="菜单"
          className="flex h-10 w-10 cursor-pointer items-center justify-center rounded-lg border border-background-700 text-foreground-900 md:hidden"
        >
          <i className={`${open ? "ri-close-line" : "ri-menu-line"} text-xl`} />
        </button>
      </nav>

      {open && (
        <div className="border-t border-background-800 bg-background-950/95 px-4 py-4 md:hidden">
          <div className="flex flex-col gap-1">
            {navLinks.map((link) => (
              <Link
                key={link.label}
                to={link.to}
                className="cursor-pointer rounded-lg px-3 py-3 text-sm font-medium text-foreground-700 hover:bg-background-800 hover:text-foreground-950"
              >
                {link.label}
              </Link>
            ))}
            <p className="mt-2 px-3 text-xs font-semibold uppercase tracking-wider text-foreground-500">漫画分类</p>
            <div className="mt-1 grid grid-cols-2 gap-1">
              {categories.map((c) => (
                <Link
                  key={c.slug}
                  to={`/category/${c.slug}`}
                  className="cursor-pointer rounded-lg bg-background-900 px-3 py-2.5 text-sm text-foreground-700 hover:bg-background-800 hover:text-foreground-950"
                >
                  {c.name}
                </Link>
              ))}
            </div>
          </div>
        </div>
      )}
    </header>
  );
}